import {useSelectableItem as $G86Z8$useSelectableItem, useSelectableCollection as $G86Z8$useSelectableCollection} from "@react-aria/selection";
import {getFocusableTreeWalker as $G86Z8$getFocusableTreeWalker} from "@react-aria/focus";
import {useState as $G86Z8$useState, useMemo as $G86Z8$useMemo} from "react";
import {useLayoutEffect as $G86Z8$useLayoutEffect, useLabels as $G86Z8$useLabels, mergeProps as $G86Z8$mergeProps, useId as $G86Z8$useId} from "@react-aria/utils";
import {useLocale as $G86Z8$useLocale} from "@react-aria/i18n";

function $parcel$export(e, n, v, s) {
  Object.defineProperty(e, n, {get: v, set: s, enumerable: true, configurable: true});
}
var $fbeca008f459f5aa$exports = {};

$parcel$export($fbeca008f459f5aa$exports, "useTab", () => $fbeca008f459f5aa$export$fdf4756d5b8ef90a);
const $2bc294289400274c$export$c5f62239608282b6 = new WeakMap();
function $2bc294289400274c$export$567fc7097e064344(state, key, role) {
    if (typeof key === 'string') key = key.replace(/\s+/g, '');
    let baseId = $2bc294289400274c$export$c5f62239608282b6.get(state);
    return `${baseId}-${role}-${key}`;
}



function $fbeca008f459f5aa$export$fdf4756d5b8ef90a(props, state, ref) {
    let { key: key , isDisabled: propsDisabled  } = props;
    let { selectionManager: manager , selectedKey: selectedKey  } = state;
    let isSelected = key === selectedKey;
    let isDisabled = propsDisabled || state.disabledKeys.has(key);
    let { itemProps: itemProps  } = $G86Z8$useSelectableItem({
        selectionManager: manager,
        key: key,
        ref: ref,
        isDisabled: isDisabled
    });
    let tabId = $2bc294289400274c$export$567fc7097e064344(state, key, 'tab');
    let tabPanelId = $2bc294289400274c$export$567fc7097e064344(state, key, 'tabpanel');
    let { tabIndex: tabIndex  } = itemProps;
    return {
        tabProps: {
            ...itemProps,
            id: tabId,
            'aria-selected': isSelected,
            'aria-disabled': isDisabled || undefined,
            'aria-controls': isSelected ? tabPanelId : undefined,
            tabIndex: isDisabled ? undefined : tabIndex,
            role: 'tab'
        }
    };
}


var $bc77009ac794dd15$exports = {};

$parcel$export($bc77009ac794dd15$exports, "useTabPanel", () => $bc77009ac794dd15$export$fae0121b5afe572d);




function $bc77009ac794dd15$export$fae0121b5afe572d(props, state, ref) {
    let [tabIndex, setTabIndex] = $G86Z8$useState(0);
    // The tabpanel should have tabIndex=0 when there are no tabbable elements within it.
    // Otherwise, tabbing from the focused tab should go directly to the first tabbable element
    // within the tabpanel.
    $G86Z8$useLayoutEffect(()=>{
        if (ref === null || ref === void 0 ? void 0 : ref.current) {
            let update = ()=>{
                // Detect if there are any tabbable elements and update the tabIndex accordingly.
                let walker = $G86Z8$getFocusableTreeWalker(ref.current, {
                    tabbable: true
                });
                setTabIndex(walker.nextNode() ? undefined : 0);
            };
            update();
            // Update when new elements are inserted, or the tabIndex/disabled attribute updates.
            let observer = new MutationObserver(update);
            observer.observe(ref.current, {
                subtree: true,
                childList: true,
                attributes: true,
                attributeFilter: [
                    'tabIndex',
                    'disabled'
                ]
            });
            return ()=>{
                observer.disconnect();
            };
        }
    }, [
        ref
    ]);
    const id = $2bc294289400274c$export$567fc7097e064344(state, state === null || state === void 0 ? void 0 : state.selectedKey, 'tabpanel');
    const tabPanelProps = $G86Z8$useLabels({
        ...props,
        id: id,
        'aria-labelledby': $2bc294289400274c$export$567fc7097e064344(state, state === null || state === void 0 ? void 0 : state.selectedKey, 'tab')
    });
    return {
        tabPanelProps: $G86Z8$mergeProps(tabPanelProps, {
            tabIndex: tabIndex,
            role: 'tabpanel',
            'aria-describedby': props['aria-describedby'],
            'aria-details': props['aria-details']
        })
    };
}


var $67cd7a6663dbdf35$exports = {};

$parcel$export($67cd7a6663dbdf35$exports, "useTabList", () => $67cd7a6663dbdf35$export$773e389e644c5874);



class $d9c38f86f4c836f2$export$15010ca3c1abe90b {
    getKeyLeftOf(key) {
        if (this.flipDirection) return this.getNextKey(key);
        else {
            if (this.orientation === 'horizontal') return this.getPreviousKey(key);
            return null;
        }
    }
    getKeyRightOf(key) {
        if (this.flipDirection) return this.getPreviousKey(key);
        else {
            if (this.orientation === 'horizontal') return this.getNextKey(key);
            return null;
        }
    }
    getKeyAbove(key) {
        if (this.orientation === 'vertical') return this.getPreviousKey(key);
        return null;
    }
    getKeyBelow(key) {
        if (this.orientation === 'vertical') return this.getNextKey(key);
        return null;
    }
    getFirstKey() {
        let key = this.collection.getFirstKey();
        if (this.disabledKeys.has(key)) key = this.getNextKey(key);
        return key;
    }
    getLastKey() {
        let key = this.collection.getLastKey();
        if (this.disabledKeys.has(key)) key = this.getPreviousKey(key);
        return key;
    }
    getNextKey(key) {
        do {
            key = this.collection.getKeyAfter(key);
            if (key == null) key = this.collection.getFirstKey();
        }while (this.disabledKeys.has(key))
        return key;
    }
    getPreviousKey(key) {
        do {
            key = this.collection.getKeyBefore(key);
            if (key == null) key = this.collection.getLastKey();
        }while (this.disabledKeys.has(key))
        return key;
    }
    constructor(collection, direction, orientation, disabledKeys = new Set()){
        this.collection = collection;
        this.flipDirection = direction === 'rtl' && orientation === 'horizontal';
        this.orientation = orientation;
        this.disabledKeys = disabledKeys;
    }
}




function $67cd7a6663dbdf35$export$773e389e644c5874(props, state, ref) {
    let { orientation: orientation = 'horizontal' , keyboardActivation: keyboardActivation = 'automatic'  } = props;
    let { collection: collection , selectionManager: manager , disabledKeys: disabledKeys  } = state;
    let { direction: direction  } = $G86Z8$useLocale();
    let delegate = $G86Z8$useMemo(()=>new $d9c38f86f4c836f2$export$15010ca3c1abe90b(collection, direction, orientation, disabledKeys)
    , [
        collection,
        disabledKeys,
        orientation,
        direction
    ]);
    let { collectionProps: collectionProps  } = $G86Z8$useSelectableCollection({
        ref: ref,
        selectionManager: manager,
        keyboardDelegate: delegate,
        selectOnFocus: keyboardActivation === 'automatic',
        disallowEmptySelection: true,
        scrollRef: ref
    });
    // Compute base id for all tabs
    let tabsId = $G86Z8$useId();
    $2bc294289400274c$export$c5f62239608282b6.set(state, tabsId);
    let tabListLabelProps = $G86Z8$useLabels({
        ...props,
        id: tabsId
    });
    return {
        tabListProps: {
            ...$G86Z8$mergeProps(collectionProps, tabListLabelProps),
            role: 'tablist',
            'aria-orientation': orientation,
            tabIndex: undefined
        }
    };
}




export {$fbeca008f459f5aa$export$fdf4756d5b8ef90a as useTab, $bc77009ac794dd15$export$fae0121b5afe572d as useTabPanel, $67cd7a6663dbdf35$export$773e389e644c5874 as useTabList};
//# sourceMappingURL=module.js.map
